/**
 * Cookies a profile starts its browser with, in the shape `--bot-cookies`
 * reads (Chrome's CDP `CookieParam`).
 *
 * BotBrowser silently skips a cookie without `url`, so a cookie here needs a
 * `url` or a `domain`, and `normalizeCookies` derives the missing `url` before
 * the list is written for the browser. Import parsers live in
 * `format/cookies.ts`.
 */

import { z } from "zod";

export const COOKIES_MAX = 5_000;

/** About 2 MB of JSON, stored inline on the profile row. */
export const COOKIES_MAX_SERIALIZED_CHARS = 2_000_000;

/** Browsers refuse a cookie whose name and value together exceed this. */
export const COOKIE_MAX_NAME_VALUE_CHARS = 4_096;

export const CookieSameSiteSchema = z.enum(["Strict", "Lax", "None"], {
	error: "SameSite must be Strict, Lax, or None",
});

// biome-ignore lint/suspicious/noControlCharactersInRegex: that is the point
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

const COOKIE_DOMAIN_PATTERN = /^\.?[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*\.?$/;

function isHttpUrl(value: string): boolean {
	try {
		const url = new URL(value);
		return url.protocol === "http:" || url.protocol === "https:";
	} catch {
		return false;
	}
}

export const CookieSchema = z
	.object({
		name: z
			.string({ error: "Cookie name must be text" })
			.min(1, "Cookie name is required")
			.refine(
				(name) => !/[;=]/.test(name) && !CONTROL_CHARACTERS.test(name),
				"Cookie name cannot contain ';', '=', or control characters",
			),
		value: z
			.string({ error: "Cookie value must be text" })
			.refine(
				(value) => !value.includes(";") && !CONTROL_CHARACTERS.test(value),
				"Cookie value cannot contain ';' or control characters",
			),
		url: z
			.string({ error: "Cookie URL must be text" })
			.max(2048, "Cookie URL must be 2048 characters or less")
			.refine(isHttpUrl, "Cookie URL must be an http:// or https:// URL")
			.optional(),
		domain: z
			.string({ error: "Cookie domain must be text" })
			.max(255, "Cookie domain must be 255 characters or less")
			.regex(COOKIE_DOMAIN_PATTERN, "Cookie domain is not a valid host name")
			.optional(),
		path: z
			.string({ error: "Cookie path must be text" })
			.max(1024, "Cookie path must be 1024 characters or less")
			.startsWith("/", "Cookie path must start with /")
			.optional(),
		secure: z.boolean({ error: "secure must be true or false" }).optional(),
		httpOnly: z.boolean({ error: "httpOnly must be true or false" }).optional(),
		sameSite: CookieSameSiteSchema.optional(),
		/** Seconds since the epoch; omitted for a session cookie. */
		expires: z
			.number({ error: "Cookie expiry must be a number" })
			.min(0, "Cookie expiry cannot be negative")
			.optional(),
	})
	.refine((cookie) => cookie.url !== undefined || cookie.domain !== undefined, {
		message: "Cookie needs a url or a domain",
		path: ["domain"],
	})
	.refine(
		(cookie) =>
			cookie.name.length + cookie.value.length <= COOKIE_MAX_NAME_VALUE_CHARS,
		{
			message: `Cookie name and value together must be ${COOKIE_MAX_NAME_VALUE_CHARS} characters or less`,
			path: ["value"],
		},
	)
	.describe("Browser cookie (CDP CookieParam)");

export type Cookie = z.infer<typeof CookieSchema>;

export const CookiesSchema = z
	.array(CookieSchema, { error: "Cookies must be a list" })
	.max(COOKIES_MAX, `Import at most ${COOKIES_MAX} cookies`)
	.refine(
		(cookies) => JSON.stringify(cookies).length <= COOKIES_MAX_SERIALIZED_CHARS,
		"Cookies are larger than 2 MB; import fewer",
	)
	.describe("Cookies loaded into the profile at launch");

/** A cookie's `url`, or the one derived from its domain, scheme, and path. */
export function cookieUrl(cookie: Cookie): string | undefined {
	if (cookie.url) return cookie.url;
	if (!cookie.domain) return undefined;
	const host = cookie.domain.replace(/^\./, "").replace(/\.$/, "");
	return `${cookie.secure ? "https" : "http"}://${host}${cookie.path ?? "/"}`;
}

/** The list with every `url` filled in, ready for `--bot-cookies`. */
export function normalizeCookies(cookies: readonly Cookie[]): Cookie[] {
	return cookies.map((cookie) =>
		cookie.url ? cookie : { ...cookie, url: cookieUrl(cookie) },
	);
}
